import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  IDispatchChartListRequest,
  IDispatchChartListResponse,
  IDispatchListRequest,
  IDispatchListResponse,
  IOutgoingCategoriesResponse,
  IPendingReceptionRequest,
  IPendingReceptionResponse,
  IPrepareDispatchChartRequest,
  IPrepareDispatchChartResponse,
  IProcessDispatchChartRequest,
  IReceptionConfirmationRequest,
  IRemoveFromChartRequest,
  IDocumentParcels,
  IParcelPreview,
  IResendParcelRequest,
  IResendParcelResponse,
  IChangeDeliveryKindManyRequest,
  IChangeDeliveryKindManyResponse,
  IGroupParcelsRequest,
  IGroupParcelsResponse,
  IUngroupParcelsRequest,
  IRefreshReceiverDataRequest,
  IRefreshReceiverDataResponse,
  IPuhSentRequest,
  IPuhSentResponse,
  IParcelDeliveryInfo,
  EParcelConfirmationFileKind,
} from "dto/Mailroom/IDispatchChart";

// Biuro podawcze — przesyłki wychodzące: kategorie/zakładki, karty nadawcze i zwrotki ZPO.
// Dedykowane endpointy (poza DocumentsView) — patrz OutgoingParcelsRepository.DispatchChart.

const dispatchChartQueryKeys = {
  categories: ["outgoing.categories"],
  dispatchList: (category: number, postGroup: number | null | undefined) => [
    "outgoing.dispatchList",
    category,
    postGroup ?? null,
  ],
  charts: (category: number | null | undefined, includeDispatched: boolean) => [
    "outgoing.charts",
    category ?? null,
    includeDispatched,
  ],
  pendingReception: (category: number | null | undefined) => [
    "outgoing.pendingReception",
    category ?? null,
  ],
  puhSent: (year: number, month: number) => ["outgoing.puhSent", year, month],
};

const getCategories = (): Promise<{ data: IOutgoingCategoriesResponse }> =>
  axiosInstance.get("outgoingParcel/categories");

const useGetOutgoingCategories = () =>
  useCustomQuery(dispatchChartQueryKeys.categories, getCategories);

const getDispatchList = (
  request: IDispatchListRequest
): Promise<{ data: IDispatchListResponse }> =>
  axiosInstance.post("outgoingParcel/dispatchList", request);

const useGetDispatchList = (request: IDispatchListRequest, enabled = true) =>
  useCustomQuery(
    dispatchChartQueryKeys.dispatchList(request.Category, request.PostGroup),
    () => getDispatchList(request),
    { enabled }
  );

const getDispatchCharts = (
  request: IDispatchChartListRequest
): Promise<{ data: IDispatchChartListResponse }> =>
  axiosInstance.post("outgoingParcel/dispatchChart/list", request);

const useGetDispatchCharts = (
  request: IDispatchChartListRequest,
  enabled = true
) =>
  useCustomQuery(
    dispatchChartQueryKeys.charts(
      request.Category,
      !!request.IncludeDispatched
    ),
    () => getDispatchCharts(request),
    { enabled }
  );

const getPendingReception = (
  request: IPendingReceptionRequest
): Promise<{ data: IPendingReceptionResponse }> =>
  axiosInstance.post("outgoingParcel/receptionConfirmation/pending", request);

const useGetPendingReception = (
  request: IPendingReceptionRequest,
  enabled = true
) =>
  useCustomQuery(
    dispatchChartQueryKeys.pendingReception(request.Category),
    () => getPendingReception(request),
    { enabled }
  );

const prepareDispatchChart = (
  request: IPrepareDispatchChartRequest
): Promise<{ data: IPrepareDispatchChartResponse }> =>
  axiosInstance.post("outgoingParcel/dispatchChart/prepare", request);

const usePrepareDispatchChart = () => useCustomMutation(prepareDispatchChart);

const processDispatchChart = (
  request: IProcessDispatchChartRequest
): Promise<{ data: unknown }> =>
  axiosInstance.post("outgoingParcel/dispatchChart/process", request);

const useProcessDispatchChart = () => useCustomMutation(processDispatchChart);

const removeFromChart = (
  request: IRemoveFromChartRequest
): Promise<{ data: unknown }> =>
  axiosInstance.post("outgoingParcel/dispatchChart/remove", request);

const useRemoveFromChart = () => useCustomMutation(removeFromChart);

const saveReceptionConfirmation = (
  request: IReceptionConfirmationRequest
): Promise<{ data: unknown }> =>
  axiosInstance.post("outgoingParcel/receptionConfirmation", request);

const useSaveReceptionConfirmation = () =>
  useCustomMutation(saveReceptionConfirmation);

const resendParcel = (
  request: IResendParcelRequest
): Promise<{ data: IResendParcelResponse }> =>
  axiosInstance.post("outgoingParcel/resend", request);

const useResendParcel = () => useCustomMutation(resendParcel);

/** Masowa zmiana sposobu nadania (port MassChangeOutgoingParcelType) — wynik per przesyłka. */
const changeDeliveryKindMany = (
  request: IChangeDeliveryKindManyRequest
): Promise<{ data: IChangeDeliveryKindManyResponse }> =>
  axiosInstance.post("outgoingParcel/changeDeliveryKindMany", request);

const useChangeDeliveryKindMany = () =>
  useCustomMutation(changeDeliveryKindMany);

/** Koperta wspólna: scalenie wybranych przesyłek „do wysyłki" w jedną przesyłkę grupującą. */
const groupParcels = (
  request: IGroupParcelsRequest
): Promise<{ data: IGroupParcelsResponse }> =>
  axiosInstance.post("outgoingParcel/group", request);

const useGroupParcels = () => useCustomMutation(groupParcels);

const ungroupParcels = (
  request: IUngroupParcelsRequest
): Promise<{ data: unknown }> =>
  axiosInstance.post("outgoingParcel/ungroup", request);

const useUngroupParcels = () => useCustomMutation(ungroupParcels);

/** „Aktualizuj dane adresata": przesyłki niewysłane dostają bieżące dane z książki adresowej. */
const refreshReceiverData = (
  request: IRefreshReceiverDataRequest
): Promise<{ data: IRefreshReceiverDataResponse }> =>
  axiosInstance.post("outgoingParcel/receiverData/refresh", request);

const useRefreshReceiverData = () => useCustomMutation(refreshReceiverData);

/** Skrzynka „Wysłane PUH": hybrydowe nadane w danym miesiącu. */
const getPuhSent = (
  request: IPuhSentRequest
): Promise<{ data: IPuhSentResponse }> =>
  axiosInstance.post("outgoingParcel/puhSent", request);

const useGetPuhSent = (request: IPuhSentRequest, enabled = true) =>
  useCustomQuery(
    dispatchChartQueryKeys.puhSent(request.Year, request.Month),
    () => getPuhSent(request),
    { enabled }
  );

const getParcelPreview = (
  parcelId: string
): Promise<{ data: IParcelPreview }> =>
  axiosInstance.get(`outgoingParcel/preview/${parcelId}`);

const useGetParcelPreview = (parcelId: string) =>
  useCustomQuery(
    ["outgoing.preview", parcelId],
    () => getParcelPreview(parcelId),
    { enabled: !!parcelId }
  );

const getDocumentParcels = (
  documentId: string
): Promise<{ data: IDocumentParcels }> =>
  axiosInstance.get(`outgoingParcel/byDocument/${documentId}`);

const useGetDocumentParcels = (documentId: string, enabled = true) =>
  useCustomQuery(
    ["outgoing.byDocument", documentId],
    () => getDocumentParcels(documentId),
    { enabled: enabled && !!documentId, staleTime: 30000 }
  );

// ── Potwierdzenia doręczenia w metryce (zwrotka ZPO / odbiór / karta nadawcza / dowody) ──
// Port z Magistratu: GetParcelReceptionConfirmation + metryka dokumentu („Podgląd ZPO",
// „Podgląd potwierdzenia nadania", UPP/UPD, dowody e-Doręczeń). Skany są szyfrowane kluczem
// firmy (deszyfrowanie na FE jak podgląd pisma), dowody elektroniczne serwer wydaje jawnie.

const getDeliveryInfo = (
  parcelId: string
): Promise<{ data: IParcelDeliveryInfo }> =>
  axiosInstance.get(`outgoingParcel/delivery/${parcelId}`);

const useGetDeliveryInfo = (parcelId: string, enabled = true) =>
  useCustomQuery(
    ["outgoing.delivery", parcelId],
    () => getDeliveryInfo(parcelId),
    { enabled: enabled && !!parcelId, staleTime: 30000 }
  );

const getConfirmationFile = (
  parcelId: string,
  kind: EParcelConfirmationFileKind
): Promise<{ data: IParcelPreview }> =>
  axiosInstance.get(`outgoingParcel/confirmationFile/${parcelId}`, {
    params: { kind },
  });

const useGetConfirmationFile = (
  parcelId: string,
  kind: EParcelConfirmationFileKind,
  enabled = true
) =>
  useCustomQuery(
    ["outgoing.confirmationFile", parcelId, kind],
    () => getConfirmationFile(parcelId, kind),
    { enabled: enabled && !!parcelId }
  );

/**
 * Jawny plik dowodu (UPP/UPD/UPO/EPO) przesyłki wychodzącej — blob do podglądu/pobrania.
 * `asPdf` — dowód XML przychodzi jako wizualizacja PDF (BE), inaczej oryginalny plik.
 */
const getParcelEvidenceFile = (
  evidenceId: string,
  asPdf = false
): Promise<{ data: Blob }> =>
  axiosInstance.get(`outgoingParcel/evidence/${evidenceId}`, {
    params: asPdf ? { asPdf: true } : undefined,
    responseType: "blob",
  });

/** Wydruk PDF kart nadawczych (blob do pobrania) — endpoint dostarcza Faza C. */
const printDispatchCharts = (chartIds: string[]): Promise<{ data: Blob }> =>
  axiosInstance.post(
    "outgoingParcel/dispatchChart/print",
    { ChartIds: chartIds },
    { responseType: "blob" }
  );

export default {
  dispatchChartQueryKeys,
  useGetOutgoingCategories,
  useGetDispatchList,
  useGetDispatchCharts,
  useGetPendingReception,
  usePrepareDispatchChart,
  useProcessDispatchChart,
  useRemoveFromChart,
  useSaveReceptionConfirmation,
  useResendParcel,
  useChangeDeliveryKindMany,
  useGroupParcels,
  useUngroupParcels,
  useRefreshReceiverData,
  useGetPuhSent,
  useGetParcelPreview,
  useGetDocumentParcels,
  useGetDeliveryInfo,
  useGetConfirmationFile,
  getParcelEvidenceFile,
  printDispatchCharts,
};
